import { useEffect, useRef, useState } from "react";
import "./App.css";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import FluidBackground from "./components/FluidBackground";

const LOGO =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_f94b530e-baf1-433b-800a-c7504cb433af/artifacts/ilorve0f_logo.webp";

const IG = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
  </svg>
);
const FB = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M14 8.5V7c0-1 .5-1.5 1.6-1.5H17V3h-2.2C12.3 3 11 4.4 11 6.8V8.5H9V11h2v10h3V11h2.2l.4-2.5H14Z" fill="currentColor" />
  </svg>
);
const X = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M17.5 4h2.6l-5.7 6.5L21 20h-5.3l-4.1-5.4L6.8 20H4.2l6.1-7L3.4 4h5.4l3.7 4.9L17.5 4Zm-.9 14.4h1.4L8 5.5H6.5l10.1 12.9Z" fill="currentColor" />
  </svg>
);
const WA = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M12 3a9 9 0 0 0-7.7 13.6L3 21l4.5-1.2A9 9 0 1 0 12 3Zm0 16.4a7.4 7.4 0 0 1-3.8-1l-.3-.2-2.7.7.7-2.6-.2-.3A7.4 7.4 0 1 1 12 19.4Zm4.2-5.5c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6 6 0 0 1-3-2.6c-.2-.4.2-.4.6-1.2.1-.1 0-.3 0-.4l-.7-1.7c-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.7.7-.8 1.6-.8 1.7s.5 2.2 2.1 3.9c2 2 2.6 2.1 3.6 2.3.6.1 1.3 0 1.5-.4.2-.4.2-.8.1-.9l-.3-.4Z" fill="currentColor" />
  </svg>
);

const SOCIALS = [
  { name: "Instagram", href: "#", Icon: IG },
  { name: "Facebook", href: "#", Icon: FB },
  { name: "X", href: "#", Icon: X },
  { name: "WhatsApp", href: "https://wa.me/918478966253", Icon: WA },
];

const MaskWord = ({ children, delay = 0 }) => (
  <span className="ji-mask">
    <motion.span
      className="ji-mask-inner"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

function App() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      inputRef.current?.focus();
      return;
    }
    setSent(true);
  };

  return (
    <div className="ji-root" data-testid="coming-soon-page">
      <FluidBackground />

      <main className="ji-stage">
        <motion.img
          src={LOGO}
          alt="Jazak Interior"
          className="ji-logo"
          initial={{ opacity: 0, y: 24, filter: "blur(14px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        />

        <motion.p
          className="ji-tagline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.55 }}
        >
          Architecture &amp; Interior Design Studio
        </motion.p>

        <h1 className="ji-title" aria-label="Coming Soon">
          <span className="ji-line">
            <MaskWord delay={0.7}>Coming Soon</MaskWord>
          </span>
        </h1>

        <motion.p
          className="ji-lede"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15 }}
        >
          A new space is taking shape. Be the first to step inside when the doors open.
        </motion.p>

        <motion.div
          className="ji-form-wrap"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {sent ? (
              <motion.div
                key="done"
                className="ji-thanks"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                You’re on the list — we’ll be in touch soon.
              </motion.div>
            ) : (
              <motion.form key="form" className="ji-form" onSubmit={submit} exit={{ opacity: 0 }}>
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="ji-input"
                  aria-label="Email address"
                />
                <button type="submit" className="ji-btn">
                  <span>Notify Me</span>
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.nav
          className="ji-socials"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.55 }}
        >
          {SOCIALS.map(({ name, href, Icon }) => (
            <a
              key={name}
              href={href}
              aria-label={name}
              className="ji-social"
              target="_blank"
              rel="noreferrer"
            >
              <Icon className="ji-social-ico" />
            </a>
          ))}
        </motion.nav>

        <motion.footer
          className="ji-copy"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.8 }}
        >
          © {new Date().getFullYear()} Jazak Interior · Est. 2019
        </motion.footer>
      </main>
    </div>
  );
}

export default App;